import React, { useEffect, useState } from "react";
import { Spin, Tag } from "antd";
import { BankOutlined, FileTextOutlined, LeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import EngagementService, {
  EngagementDto,
} from "../../../ClientView/services/EngagementService";
import styles from "./EngagementDetails.module.css";

// No GET /api/engagement/:id endpoint exists on the backend.
// Fast path: engagement object carried via React Router location.state when
// navigating from EngagementList — no API call needed.
// Fallback path: when state is absent (direct URL, bookmark, F5), fetches
// GET /api/engagement (the full tenant list) and finds the record by numeric ID
// client-side. Constraint: the endpoint returns ALL engagements for the tenant
// with no pagination parameter. This works for any realistic tenant dataset, but
// if pagination is ever added to the endpoint the fallback would only search
// the first page — a GET /api/engagement/:id endpoint would be the proper fix.

const DETAIL_TABS = [
  { key: "entities",  label: "Entities"  },
  { key: "projects",  label: "Projects"  },
  { key: "auditLog",  label: "Audit Log" },
];

const TYPE_COLORS: Record<string, string> = {
  Audit:      "#8B2500",
  Tax:        "#2563eb",
  Compliance: "#7c3aed",
  Consulting: "#16a34a",
  Advisory:   "#0891b2",
};

function statusColor(status: string) {
  if (status === "Active")    return "#22c55e";
  if (status === "Draft")     return "#f59e0b";
  if (status === "Completed") return "#94a3b8";
  return "#ADADAD";
}

// ── Not-found state ───────────────────────────────────────────────────────────

const NotFound: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className={styles.pageWrap}>
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>🔍</div>
        <div className={styles.emptyText}>Engagement not found</div>
        <div className={styles.emptySubtext}>
          No engagement with this ID was found in your tenant's engagement list.
        </div>
        <button
          className={styles.btnOutline}
          onClick={() => navigate("/engagements")}
        >
          Back to Engagements
        </button>
      </div>
    </div>
  );
};

// ── EngagementDetails ─────────────────────────────────────────────────────────

export const EngagementDetails: React.FC = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { id }    = useParams<{ id: string }>();

  // Fast path: already have the object from list navigation (no fetch needed).
  const stateEngagement: EngagementDto | undefined = location.state?.engagement;

  const [resolvedEngagement, setResolvedEngagement] = useState<EngagementDto | null>(
    stateEngagement ?? null,
  );
  // isFetching starts true only when route state is absent — the fast path never shows a spinner.
  const [isFetching, setIsFetching] = useState(!stateEngagement);
  const [activeTab, setActiveTab]   = useState("entities");

  useEffect(() => {
    // Fast path: state already carried the engagement; skip the API call.
    if (stateEngagement) return;

    // Fallback: no route state (direct URL / bookmark / F5).
    // Fetch the full list and find by numeric ID client-side.
    // See constraint note at the top of this file regarding pagination.
    let cancelled = false;
    setIsFetching(true);

    EngagementService.getEngagements()
      .then((list) => {
        if (cancelled) return;
        const numericId = parseInt(id ?? "", 10);
        const found = list.find((e) => e.id === numericId) ?? null;
        setResolvedEngagement(found);
      })
      .catch(() => {
        if (cancelled) return;
        setResolvedEngagement(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsFetching(false);
      });

    return () => { cancelled = true; };
  // stateEngagement is derived from location.state on first render; id comes from useParams.
  // Both are stable for the lifetime of this URL — deps are correct.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── Loading (fallback path only) ──────────────────────────────────────────
  if (isFetching) {
    return (
      <div className={styles.pageWrap}>
        <div className={styles.loadingState}><Spin /></div>
      </div>
    );
  }

  // ── Not found (fetch completed, no matching record) ───────────────────────
  if (!resolvedEngagement) return <NotFound />;

  const engagement = resolvedEngagement;

  const color = statusColor(engagement.status);
  const typeColor = TYPE_COLORS[engagement.engagementType] ?? "#64748b";

  return (
    <div className={styles.pageWrap}>

      {/* ── Back link ── */}
      <button className={styles.backLink} onClick={() => navigate("/engagements")}>
        <LeftOutlined style={{ marginRight: 6, fontSize: 12 }} />
        Back to Engagements
      </button>

      {/* ── Header ── */}
      <div className={styles.detailHead}>
        <div className={styles.headLeft}>
          <div className={styles.titleRow}>
            <h1 className={styles.pageTitle}>{engagement.description}</h1>
            <Tag
              style={{
                color,
                borderColor: color,
                background: "transparent",
                borderRadius: 4,
                fontWeight: 600,
                fontSize: 12,
                marginLeft: 12,
                flexShrink: 0,
              }}
            >
              {engagement.status}
            </Tag>
          </div>
          <div className={styles.metaLine}>
            <span className={styles.mono}>{engagement.engagementId}</span>
            <span className={styles.metaSep}>·</span>
            <span
              style={{
                color: typeColor,
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              {engagement.engagementType}
            </span>
            <span className={styles.metaSep}>·</span>
            Tax Year {engagement.taxYear}
          </div>
        </div>
        <div className={styles.headRight}>
          {/* No PATCH /api/engagement/:id endpoint — button is present but non-functional */}
          <button
            className={styles.btnOutline}
            title="Edit endpoint not yet available"
            onClick={() =>
              alert("Edit is not yet wired — no PATCH /api/engagement/:id endpoint.")
            }
          >
            Edit
          </button>
          {/* No status-toggle endpoint — button is present but non-functional */}
          <button
            className={styles.btnDanger}
            title="Deactivate endpoint not yet available"
            onClick={() =>
              alert(
                "Deactivate is not yet wired — no status endpoint for engagements.",
              )
            }
          >
            Deactivate
          </button>
          {/* 2.4 Gantt Timeline is flagged as a follow-up — not built in this pass */}
          <button
            className={styles.btnOutlineAccent}
            title="Gantt Timeline (2.4) — flagged as a follow-up, not yet built"
            onClick={() =>
              alert(
                "Show Timeline (2.4 Gantt) is a follow-up component — not built in this pass.",
              )
            }
          >
            Show Timeline
          </button>
        </div>
      </div>

      {/* ── Info cards ── */}
      <div className={styles.infoGrid}>

        {/* Card 1: Engagement Details */}
        <div className={styles.infoCard}>
          <div className={styles.infoCardTitle}>Engagement Details</div>
          <div className={styles.fieldGrid}>
            <div className={styles.fieldItem}>
              <div className={styles.fieldLabel}>Type</div>
              <div className={styles.fieldValue}>{engagement.engagementType}</div>
            </div>
            <div className={styles.fieldItem}>
              <div className={styles.fieldLabel}>Tax Year</div>
              <div className={styles.fieldValue}>{engagement.taxYear}</div>
            </div>
            <div className={styles.fieldItem}>
              <div className={styles.fieldLabel}>Engagement ID</div>
              <div className={styles.fieldValue}>
                <span className={styles.mono}>{engagement.engagementId}</span>
              </div>
            </div>
            <div className={styles.fieldItem}>
              <div className={styles.fieldLabel}>Status</div>
              <div className={styles.fieldValue}>
                <Tag
                  style={{
                    color,
                    borderColor: color,
                    background: "transparent",
                    borderRadius: 4,
                    fontWeight: 500,
                    fontSize: 11,
                  }}
                >
                  {engagement.status}
                </Tag>
              </div>
            </div>
            {/* startDate/endDate not in EngagementDto */}
            <div className={styles.fieldItemGap}>
              <div className={styles.fieldLabel}>Start Date</div>
              <div className={styles.fieldValueGap}>
                — <span className={styles.gapNote}>not in schema</span>
              </div>
            </div>
            <div className={styles.fieldItemGap}>
              <div className={styles.fieldLabel}>End Date</div>
              <div className={styles.fieldValueGap}>
                — <span className={styles.gapNote}>not in schema</span>
              </div>
            </div>
            {/* leadPartner not in EngagementDto */}
            <div className={styles.fieldItemGap}>
              <div className={styles.fieldLabel}>Lead Partner</div>
              <div className={styles.fieldValueGap}>
                — <span className={styles.gapNote}>not in schema</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Linked Entities & Notes */}
        <div className={styles.infoCard}>
          <div className={styles.infoCardTitle}>Linked Entities &amp; Partnerships</div>
          {/* TODO: implement GET /api/engagement/:id/entities */}
          <div className={styles.infoEmptyState}>
            <div className={styles.infoEmptyText}>No linked entities</div>
            <div className={styles.infoEmptySubtext}>
              Engagement-entity relationships are not yet queryable. Add a{" "}
              <code>GET /api/engagement/:id/entities</code> endpoint to populate this panel.
            </div>
          </div>

          <div className={styles.infoCardDivider} />

          <div className={styles.infoCardTitle} style={{ marginTop: 16 }}>Notes</div>
          {/* notes field not in EngagementDto */}
          <div className={styles.fieldItemGap}>
            <div className={styles.fieldValueGap}>
              — <span className={styles.gapNote}>not in schema</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className={styles.tabs}>
        {DETAIL_TABS.map((t) => (
          <button
            key={t.key}
            className={activeTab === t.key ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}
      <div className={styles.tabContent}>
        {activeTab === "entities" && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><BankOutlined style={{ fontSize: 32, color: '#ADADAD' }} /></div>
            <div className={styles.emptyText}>No entities in scope</div>
            <div className={styles.emptySubtext}>
              {/* TODO: implement GET /api/engagement/:id/entities */}
              Engagement-entity relationship querying is not yet implemented in the backend.
            </div>
          </div>
        )}
        {activeTab === "projects" && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📁</div>
            <div className={styles.emptyText}>No projects yet</div>
            <div className={styles.emptySubtext}>
              {/* TODO: implement GET /api/project?engagementId=:id */}
              Project listing scoped to an engagement is not yet supported in the backend.
            </div>
          </div>
        )}
        {activeTab === "auditLog" && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><FileTextOutlined style={{ fontSize: 32, color: '#ADADAD' }} /></div>
            <div className={styles.emptyText}>No audit history available</div>
            <div className={styles.emptySubtext}>
              {/* TODO: implement GET /api/engagement/:id/audit */}
              Engagement-level audit logging is not yet implemented in the backend.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
